// JSONContainer.tsx
import React, { ReactNode } from 'react';
import { Box, IconButton, Paper, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import './jsonContainer.css';
import { useCopyText } from '../../../hooks/useCopyText';
import CustomButton from '../../MasterComponent/Button/CustomButton';

export interface PolicyData {
  name: string;
  suggestedName?: string;
  description: ReactNode;
  json?: string | undefined;
  copied: boolean;
}

interface JSONContainerProps {
  index: number;
  policy: PolicyData;
  activeStep: number;
  totalSteps: number;
  onCopyJson: (index: number) => void;
  onNext?: () => void;
  onBack: () => void;
  stepType?: string;
  suggestedNameType?: string;
}

export const JSONContainer: React.FC<JSONContainerProps> = ({
  index,
  policy,
  activeStep,
  totalSteps,
  onCopyJson,
  onNext,
  onBack,
  suggestedNameType = 'policy',
  stepType = 'Policy',
}) => {
  const { isCopied, copyToClipboard } = useCopyText();

  return (
    <Box className="policyContentBox">
      <p className="policyDescription font-14px fw-400">{policy.description}</p>

      {policy.suggestedName && (
        <>
          <p className="suggestedNameLabel font-12px">
            Suggested {suggestedNameType} name:
          </p>
          <Box className="suggestedNameBox">
            <div className="suggestedName font-13px ">
              {policy.suggestedName}
            </div>
            <Tooltip title="Copy suggested name">
              <IconButton
                size="small"
                onClick={() =>
                  copyToClipboard(policy.suggestedName || 'policy')
                }
                className="copyNameButton"
              >
                {isCopied ? (
                  <CheckCircleOutlineIcon fontSize="small" />
                ) : (
                  <ContentCopyIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        </>
      )}

      {policy.json && (
        <>
          {' '}
          <p className="suggestedNameLabel font-12px">Policy JSON:</p>
          <Paper variant="outlined" className="jsonPaper">
            <pre className="jsonContent">{policy.json}</pre>
            <CustomButton
              buttonType={policy.copied ? 'success' : 'contained'}
              startIcon={
                policy.copied ? <CheckCircleOutlineIcon /> : <ContentCopyIcon />
              }
              onClickHandler={() => onCopyJson(index)}
              style={{ marginTop: '1rem', marginBottom: '1rem' }}
            >
              {policy.copied ? 'Copied!' : 'Copy JSON'}
            </CustomButton>
          </Paper>{' '}
        </>
      )}

      {onNext && (
        <Box className="navigationButtonsContainer">
          <CustomButton
            disabled={activeStep === 0}
            onClickHandler={onBack}
            buttonType="outlined"
          >
            Prev {stepType}
          </CustomButton>
          <Box className="rightButtons">
            {activeStep < totalSteps - 1 && (
              <CustomButton buttonType="contained" onClickHandler={onNext}>
                Next {stepType}
              </CustomButton>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};
